import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import {
  Mail,
  Clock,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Trash2,
  Copy,
  Check,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { Button } from "../ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";

const STATUS_STYLE = {
  pending: { bg: "bg-amber-100 text-amber-800 ring-amber-200", icon: Clock },
  accepted: {
    bg: "bg-emerald-100 text-emerald-800 ring-emerald-200",
    icon: CheckCircle2,
  },
  expired: { bg: "bg-zinc-100 text-zinc-600 ring-zinc-200", icon: XCircle },
};

const TABS = ["pending", "accepted", "expired"];

export default function PendingInvitations({ refreshKey = 0 }) {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/users/invitations?status=${tab}`);
      setItems(data.items || []);
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const resend = async (id) => {
    try {
      await api.post(`/users/invitations/${id}/resend`);
      toast.success(t("users.invitations.resent"));
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const revoke = async (id) => {
    try {
      await api.delete(`/users/invitations/${id}`);
      toast.success(t("users.invitations.revoked"));
      setConfirmRevoke(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const copyToken = async (id, token) => {
    try {
      await navigator.clipboard.writeText(token);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1200);
    } catch {
      /* no-op */
    }
  };

  const pendingCount = items.length;

  return (
    <section
      className="rounded-xl border border-zinc-200 bg-white shadow-sm"
      data-testid="pending-invitations-panel"
    >
      <button
        type="button"
        onClick={() => setCollapsed((v) => !v)}
        className="flex w-full items-center justify-between gap-3 px-5 py-3 text-left"
        data-testid="pending-invitations-toggle"
      >
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-violet-500 to-indigo-600 text-white">
            <Mail className="h-4 w-4" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-zinc-900">
              {t("users.invitations.title")}
            </h3>
            <p className="text-[11px] text-zinc-500">
              {tab === "pending"
                ? t("users.invitations.pending_hint", { count: pendingCount })
                : t("users.invitations.subtitle")}
            </p>
          </div>
        </div>
        {collapsed ? (
          <ChevronDown className="h-4 w-4 text-zinc-400" />
        ) : (
          <ChevronUp className="h-4 w-4 text-zinc-400" />
        )}
      </button>

      {!collapsed && (
        <div className="border-t border-zinc-100 p-5 pt-4">
          {/* Tabs */}
          <div className="mb-3 flex flex-wrap gap-1.5">
            {TABS.map((s) => {
              const active = tab === s;
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => setTab(s)}
                  data-testid={`pending-invitations-tab-${s}`}
                  className={[
                    "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
                    active
                      ? "border-violet-600 bg-violet-600 text-white"
                      : "border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50",
                  ].join(" ")}
                >
                  {t(`users.invitations.status_${s}`)}
                </button>
              );
            })}
          </div>

          {loading && (
            <div className="py-6 text-center text-sm text-zinc-500">
              {t("common.loading")}
            </div>
          )}

          {!loading && items.length === 0 && (
            <div
              className="rounded-lg border border-dashed border-zinc-200 py-8 text-center"
              data-testid="pending-invitations-empty"
            >
              <Mail className="mx-auto h-6 w-6 text-zinc-300" />
              <p className="mt-2 text-xs text-zinc-500">
                {t(`users.invitations.empty_${tab}`)}
              </p>
            </div>
          )}

          <ul className="space-y-2">
            {items.map((inv) => {
              const st = STATUS_STYLE[inv.status] || STATUS_STYLE.pending;
              const StIcon = st.icon;
              return (
                <li
                  key={inv.id}
                  data-testid={`pending-invitation-${inv.id}`}
                  className="flex flex-wrap items-start justify-between gap-3 rounded-lg border border-zinc-100 bg-zinc-50/40 px-3 py-2.5"
                >
                  <div className="min-w-0 flex-1">
                    <div className="mb-0.5 flex flex-wrap items-center gap-2">
                      <span
                        className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 ${st.bg}`}
                      >
                        <StIcon className="h-3 w-3" />
                        {t(`users.invitations.status_${inv.status}`)}
                      </span>
                      <span className="inline-flex rounded-full bg-zinc-200 px-2 py-0.5 text-[10px] font-semibold text-zinc-700">
                        {inv.role === "super_admin"
                          ? t("users.role_super_admin")
                          : t("users.role_user")}
                      </span>
                      <span className="text-[10px] text-zinc-500">
                        {t("users.invitations.expires")}{" "}
                        {inv.expires_at
                          ? new Date(inv.expires_at).toLocaleDateString()
                          : "—"}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Mail className="h-3.5 w-3.5 text-zinc-400" />
                      <span className="truncate font-medium text-zinc-900">
                        {inv.email}
                      </span>
                      {inv.name && (
                        <span className="text-xs text-zinc-500">
                          — {inv.name}
                        </span>
                      )}
                    </div>
                    {inv.invited_by_name && (
                      <div className="mt-0.5 text-[11px] text-zinc-500">
                        {t("users.invitations.invited_by")}{" "}
                        {inv.invited_by_name}
                      </div>
                    )}
                  </div>

                  {inv.status === "pending" && (
                    <div className="flex shrink-0 items-center gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => copyToken(inv.id, inv.token)}
                        data-testid={`pending-invitation-copy-${inv.id}`}
                        className="h-7 text-[11px]"
                        title={t("users.invitations.copy_token")}
                      >
                        {copiedId === inv.id ? (
                          <>
                            <Check className="mr-1 h-3 w-3 text-emerald-600" />
                            {t("users.invitations.copied")}
                          </>
                        ) : (
                          <>
                            <Copy className="mr-1 h-3 w-3" />
                            {t("users.invitations.copy")}
                          </>
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => resend(inv.id)}
                        data-testid={`pending-invitation-resend-${inv.id}`}
                        className="h-7 text-[11px]"
                      >
                        <RefreshCw className="mr-1 h-3 w-3" />
                        {t("users.invitations.resend")}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setConfirmRevoke(inv.id)}
                        data-testid={`pending-invitation-revoke-${inv.id}`}
                        className="h-7 text-[11px] text-red-700 hover:bg-red-50"
                        title={t("users.invitations.revoke")}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <AlertDialog
        open={!!confirmRevoke}
        onOpenChange={(o) => !o && setConfirmRevoke(null)}
      >
        <AlertDialogContent data-testid="pending-invitation-revoke-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("users.invitations.revoke_title")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("users.invitations.revoke_desc")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => revoke(confirmRevoke)}
              className="bg-red-600 hover:bg-red-700"
              data-testid="pending-invitation-revoke-confirm-btn"
            >
              {t("common.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
